/**
 * FormImportModal — import layoutu FORMULARZA z pliku eksportu (przeniesienie layoutu z innego
 * tenanta). Wspólny dla kartotek systemowych, klas dokumentów i kartotek własnych.
 *
 * Kroki:
 *  1. 'pick'    — wybór pliku JSON (przeciągnij albo „Wybierz plik"); plik czytany w przeglądarce
 *                 i od razu wysyłany do podglądu (`?dryRun=true`, nic się nie zapisuje).
 *  2. 'preview' — plik + kartoteka źródłowa, liczniki (wczytane / z ostrzeżeniem / pominięte),
 *                 lista pozycji pogrupowana sekcjami z powodem, szkic formularza („Podgląd
 *                 formularza"), nazwa nowego layoutu. „Importuj" zapisuje NOWY layout.
 *
 * Ocena pozycji zawsze po stronie serwera (dane TEGO tenanta) — plik jest niezaufany.
 * Po zapisie rodzic przełącza kreator na nowy layout (`onImported`).
 */

import '@/components/modules/layout-elements';

import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type {
  FormImportCreateResponse,
  FormImportPreviewResponse,
  FormImportReportEntry,
  FormImportSection,
} from '@saas/shared';
import { useMutation } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import { AlertTriangle, CheckCircle2, Eye, FileJson, XCircle } from 'lucide-react';

import { Alert } from '@/components/Alert';
import { Button } from '@/components/Button';
import { LayoutElementRegistry } from '@/components/fields/LayoutElementRegistry';
import { Input } from '@/components/Input';
import { Modal } from '@/components/Modal';
import { PreviewModal } from '@/components/preview/PreviewModal';
import { useToast } from '@/contexts/ToastContext';
import { api } from '@/lib/api';
import { cn } from '@/lib/utils';

import { FormLayoutSketch } from './FormLayoutSketch';

const P = 'tenant.formTransfer';
const SECTION_ORDER: FormImportSection[] = ['tabs', 'containers', 'fields', 'blocks'];

export interface FormImportModalProps {
  open: boolean;
  onClose: () => void;
  /** Baza tras layoutów: `/system-modules/<slug>/layouts` albo `/modules/<id>/layouts`. */
  endpointBase: string;
  /** Wywoływane po zapisie — rodzic odświeża listę i przełącza kreator na nowy layout. */
  onImported: (layoutId: string) => void;
}

type Step = 'pick' | 'preview';

function errorCode(err: unknown): string | null {
  if (isAxiosError(err)) {
    const data = err.response?.data as { error?: string } | undefined;
    return data?.error ?? (err.response?.status === 403 ? 'FORBIDDEN' : null);
  }
  return null;
}

const ERROR_KEYS: Record<string, string> = {
  INVALID_FORMAT: 'invalidFormat',
  UNSUPPORTED_FORMAT_VERSION: 'unsupportedVersion',
  INVALID_FILE: 'invalidFile',
  WRONG_KARTOTEKA: 'wrongKartoteka',
  PAYLOAD_TOO_LARGE: 'tooLarge',
  FORBIDDEN: 'permissionDenied',
  NOTHING_TO_IMPORT: 'nothingToImport',
  NAME_TAKEN: 'nameTakenSave',
};

export function FormImportModal({ open, onClose, endpointBase, onImported }: FormImportModalProps) {
  const { t } = useTranslation();
  const toast = useToast();

  const [step, setStep] = useState<Step>('pick');
  const [fileName, setFileName] = useState<string | null>(null);
  const [fileContent, setFileContent] = useState<unknown>(null);
  const [preview, setPreview] = useState<FormImportPreviewResponse | null>(null);
  const [name, setName] = useState('');
  const [fileError, setFileError] = useState<string | null>(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const [onlyProblems, setOnlyProblems] = useState(true);
  const [sketchOpen, setSketchOpen] = useState(false);

  const errorMessage = (code: string | null): string =>
    t(`${P}.errors.${(code && ERROR_KEYS[code]) || 'generic'}`);

  function reset() {
    setStep('pick');
    setFileName(null);
    setFileContent(null);
    setPreview(null);
    setName('');
    setFileError(null);
    setIsDragOver(false);
    setOnlyProblems(true);
    setSketchOpen(false);
  }

  const previewMutation = useMutation({
    mutationFn: async (content: unknown) =>
      (
        await api.post<FormImportPreviewResponse>(`${endpointBase}/form-import?dryRun=true`, {
          file: content,
        })
      ).data,
    onSuccess: (data) => {
      setPreview(data);
      setName(data.proposedName);
      setStep('preview');
    },
    onError: (err) => setFileError(errorMessage(errorCode(err))),
  });

  const importMutation = useMutation({
    mutationFn: async () =>
      (
        await api.post<FormImportCreateResponse>(`${endpointBase}/form-import`, {
          file: fileContent,
          name: name.trim(),
        })
      ).data,
    onSuccess: ({ layout }) => {
      toast.show({
        variant: 'success',
        title: t(`${P}.toasts.imported`, { name: layout.name }),
        description: t(`${P}.toasts.importedHint`),
      });
      reset();
      onImported(layout.id);
      onClose();
    },
    onError: (err) => {
      const code = errorCode(err);
      // Na etapie zapisu plik jest już wczytany — nieznany błąd to „nie udało się zapisać".
      toast.show({
        variant: 'error',
        title: code && ERROR_KEYS[code] ? errorMessage(code) : t(`${P}.errors.saveFailed`),
      });
    },
  });

  const running = previewMutation.isPending || importMutation.isPending;

  // Zajęta nazwa nie jest zmieniana po cichu — okno ostrzega i blokuje „Importuj", dopóki
  // użytkownik nie poda wolnej nazwy (decyzja właścicielki 2026-10-21). Serwer sprawdza to
  // samo przy zapisie (409 NAME_TAKEN).
  const takenNames = useMemo(() => new Set(preview?.takenNamesLower ?? []), [preview]);
  const nameTaken = takenNames.has(name.trim().toLowerCase());

  function handleClose() {
    if (running) return;
    reset();
    onClose();
  }

  async function pickFile(file: File | null) {
    // W trakcie sprawdzania pliku nie przyjmujemy drugiego (upuszczenie omija `disabled` inputu) —
    // inaczej raport pierwszego pliku mógłby trafić obok treści drugiego.
    if (!file || running) return;
    setFileError(null);
    setFileName(file.name);
    let parsed: unknown;
    try {
      parsed = JSON.parse(await file.text());
    } catch {
      setFileError(t(`${P}.errors.notJson`));
      return;
    }
    setFileContent(parsed);
    previewMutation.mutate(parsed);
  }

  const grouped = useMemo(() => {
    const entries = (preview?.report.entries ?? []).filter(
      (e) => !onlyProblems || e.status !== 'loaded',
    );
    return SECTION_ORDER.map((section) => ({
      section,
      entries: entries.filter((e) => e.section === section),
    })).filter((g) => g.entries.length > 0);
  }, [preview, onlyProblems]);

  const entryLabel = (e: FormImportReportEntry): string => {
    if (e.label) return e.label;
    const def = e.elementType ? LayoutElementRegistry.get(e.elementType) : undefined;
    if (def?.label) return def.label;
    return e.elementType ? t(`${P}.elementTypes.${e.elementType}`, e.elementType) : '—';
  };

  const footer =
    step === 'preview' && preview ? (
      <>
        <p className="mr-auto text-sm text-text-secondary" data-testid="form-import-save-as">
          {t(`${P}.saveAs`)}{' '}
          <span className="font-medium text-text-primary">
            {name.trim() || preview.proposedName}
          </span>
        </p>
        <Button variant="ghost" onClick={handleClose} disabled={running}>
          {t('common.cancel')}
        </Button>
        <Button
          variant="primary"
          onClick={() => importMutation.mutate()}
          loading={importMutation.isPending}
          disabled={!preview.importable || name.trim().length === 0 || nameTaken || running}
          data-testid="form-import-submit"
        >
          {t(`${P}.submit`)}
        </Button>
      </>
    ) : (
      <Button variant="ghost" onClick={handleClose} disabled={running}>
        {t('common.cancel')}
      </Button>
    );

  return (
    <>
      <Modal
        open={open}
        onClose={handleClose}
        title={t(`${P}.title`)}
        description={step === 'pick' ? t(`${P}.description`) : undefined}
        size="lg"
        footer={footer}
        closeOnBackdrop={!running}
        closeOnEsc={!running}
        testId="form-import-modal"
      >
        {step === 'pick' ? (
          <div className="space-y-3" data-testid="form-import-step-pick">
            <div
              onDragOver={(ev) => {
                ev.preventDefault();
                setIsDragOver(true);
              }}
              onDragLeave={() => setIsDragOver(false)}
              onDrop={(ev) => {
                ev.preventDefault();
                setIsDragOver(false);
                void pickFile(ev.dataTransfer.files?.[0] ?? null);
              }}
              className={cn(
                'rounded-lg border-2 border-dashed p-8 text-center',
                isDragOver
                  ? 'border-accent-500 bg-accent-50 dark:bg-accent-900/20'
                  : 'border-border-secondary',
              )}
              data-testid="form-import-dropzone"
            >
              <FileJson size={36} className="mx-auto text-text-tertiary" aria-hidden="true" />
              <p className="mt-3 text-sm text-text-primary">
                {previewMutation.isPending ? t(`${P}.checking`) : (fileName ?? t(`${P}.dropHint`))}
              </p>
              <label className="mt-3 inline-block cursor-pointer text-sm text-accent-700 hover:underline dark:text-accent-300">
                {/* Brak prymitywu file-input w bibliotece — ukryty natywny input jest tu kanoniczny
                    (ten sam wzorzec co AttachmentsSection i BugReportWidget). */}
                {/* eslint-disable-next-line no-restricted-syntax */}
                <input
                  type="file"
                  accept=".json,application/json"
                  className="hidden"
                  disabled={running}
                  onChange={(ev) => {
                    void pickFile(ev.target.files?.[0] ?? null);
                    ev.target.value = '';
                  }}
                  data-testid="form-import-file-input"
                />
                {t(`${P}.browse`)}
              </label>
            </div>
            {fileError ? (
              <Alert tone="error" data-testid="form-import-file-error">
                {fileError}
              </Alert>
            ) : null}
          </div>
        ) : preview ? (
          <div className="space-y-4" data-testid="form-import-step-preview">
            <div className="flex items-center gap-3 rounded-lg bg-bg-secondary px-3 py-2">
              <FileJson className="h-5 w-5 shrink-0 text-text-secondary" aria-hidden="true" />
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm text-text-primary">{fileName}</p>
                <p className="text-xs text-text-secondary" data-testid="form-import-source">
                  {t(`${P}.sourceLine`, {
                    kartoteka: preview.source.kartotekaLabel ?? preview.source.kartotekaKey,
                    name: preview.source.name,
                  })}
                  {preview.source.exportedAt
                    ? ` · ${t(`${P}.exportedAt`, {
                        date: new Date(preview.source.exportedAt).toLocaleDateString('pl-PL'),
                      })}`
                    : ''}
                </p>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={reset}
                disabled={running}
                data-testid="form-import-change-file"
              >
                {t(`${P}.changeFile`)}
              </Button>
            </div>

            <div className="space-y-2">
              <Input
                label={t(`${P}.nameLabel`)}
                hint={nameTaken ? undefined : t(`${P}.nameHint`)}
                error={nameTaken ? t(`${P}.errors.nameTaken`, { name: name.trim() }) : undefined}
                value={name}
                maxLength={100}
                onChange={(e) => setName(e.target.value)}
                data-testid="form-import-name"
              />
              {nameTaken && preview.suggestedName ? (
                <Button
                  variant="tertiary"
                  size="sm"
                  onClick={() => setName(preview.suggestedName)}
                  data-testid="form-import-use-suggested"
                >
                  {t(`${P}.useSuggestedName`, { name: preview.suggestedName })}
                </Button>
              ) : null}
            </div>

            <div className="grid grid-cols-3 gap-2" data-testid="form-import-counts">
              <CountTile
                tone="success"
                label={t(`${P}.counts.loaded`)}
                value={preview.report.counts.loaded}
              />
              <CountTile
                tone="warning"
                label={t(`${P}.counts.warning`)}
                value={preview.report.counts.loadedWithWarning}
              />
              <CountTile
                tone="danger"
                label={t(`${P}.counts.skipped`)}
                value={preview.report.counts.skipped}
              />
            </div>

            {!preview.importable ? (
              <Alert tone="warning" data-testid="form-import-nothing">
                {t(`${P}.errors.nothingToImport`)}
              </Alert>
            ) : null}

            <div className="flex items-center justify-between gap-2">
              <div className="flex gap-1" role="group" aria-label={t(`${P}.filterLabel`)}>
                <Button
                  variant={onlyProblems ? 'tertiary' : 'ghost'}
                  size="sm"
                  onClick={() => setOnlyProblems(true)}
                  aria-pressed={onlyProblems}
                  data-testid="form-import-filter-problems"
                >
                  {t(`${P}.filterProblems`)}
                </Button>
                <Button
                  variant={onlyProblems ? 'ghost' : 'tertiary'}
                  size="sm"
                  onClick={() => setOnlyProblems(false)}
                  aria-pressed={!onlyProblems}
                  data-testid="form-import-filter-all"
                >
                  {t(`${P}.filterAll`)}
                </Button>
              </div>
              <Button
                variant="secondary"
                size="sm"
                leftIcon={<Eye className="h-4 w-4" />}
                onClick={() => setSketchOpen(true)}
                data-testid="form-import-sketch"
              >
                {t(`${P}.sketchButton`)}
              </Button>
            </div>

            <div className="space-y-3" data-testid="form-import-report">
              {grouped.length === 0 ? (
                <p className="text-sm text-text-secondary">{t(`${P}.noProblems`)}</p>
              ) : (
                grouped.map((g) => (
                  <section key={g.section} data-testid={`form-import-section-${g.section}`}>
                    <h3 className="mb-1 text-xs font-semibold text-text-secondary">
                      {t(`${P}.sections.${g.section}`)}
                    </h3>
                    <ul className="divide-y divide-border-primary">
                      {g.entries.map((e, i) => (
                        <li key={`${e.elementId ?? ''}-${i}`} className="flex gap-2 py-1.5 text-sm">
                          <StatusIcon status={e.status} />
                          <div className="min-w-0">
                            <p className="text-text-primary">{entryLabel(e)}</p>
                            {e.reason ? (
                              <p className="text-xs text-text-secondary">
                                {t(`${P}.reasons.${e.reason}`)}
                              </p>
                            ) : null}
                          </div>
                        </li>
                      ))}
                    </ul>
                  </section>
                ))
              )}
            </div>

            {preview.hadAssignments ? (
              <Alert tone="info" data-testid="form-import-assignments">
                {t(`${P}.assignmentsInfo`)}
              </Alert>
            ) : null}
          </div>
        ) : null}
      </Modal>

      {preview ? (
        <PreviewModal
          open={sketchOpen}
          onClose={() => setSketchOpen(false)}
          title={t(`${P}.sketchTitle`, { name: name.trim() || preview.proposedName })}
        >
          <div className="h-full overflow-auto p-5">
            <FormLayoutSketch
              shape={preview.shape}
              config={preview.sourceConfig}
              entries={preview.report.entries}
              fieldLabels={preview.fieldLabels}
            />
          </div>
        </PreviewModal>
      ) : null}
    </>
  );
}

function CountTile({
  tone,
  label,
  value,
}: {
  tone: 'success' | 'warning' | 'danger';
  label: string;
  value: number;
}) {
  const cls = {
    success: 'bg-green-50 text-green-800 dark:bg-green-900/20 dark:text-green-300',
    warning: 'bg-amber-50 text-amber-800 dark:bg-amber-900/20 dark:text-amber-300',
    danger: 'bg-red-50 text-red-800 dark:bg-red-900/20 dark:text-red-300',
  }[tone];
  return (
    <div className={cn('rounded-lg px-3 py-2', cls)} data-testid={`form-import-count-${tone}`}>
      <p className="text-xs">{label}</p>
      <p className="text-xl font-semibold">{value}</p>
    </div>
  );
}

function StatusIcon({ status }: { status: FormImportReportEntry['status'] }) {
  if (status === 'skipped') {
    return <XCircle className="mt-0.5 h-4 w-4 shrink-0 text-red-600" aria-hidden="true" />;
  }
  if (status === 'loaded_with_warning') {
    return <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" aria-hidden="true" />;
  }
  return <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-green-600" aria-hidden="true" />;
}
